<template>
    <section class="watchlist-details">
  
      <div class="details-toolbar">
        <div class="toolbar-controls">
          <label class="watchlist-search">
            <span class="material-icons-outlined" aria-hidden="true">
              search
            </span>

            <input
              v-model="searchText"
              type="search"
              placeholder="Search watchlist name"
              aria-label="Search watchlists by name"
            />
          </label>

          <select
            v-model="statusFilter"
            class="status-filter"
            aria-label="Filter watchlists by status"
          >
            <option value="">
              All statuses
            </option>

            <option value="published">
              Published
            </option>

            <option value="inReview">
              In Review
            </option>

            <option value="draft">
              Draft
            </option>

            <option value="archived">
              Archived
            </option>
          </select>

          <button
            type="button"
            class="refresh-button"
            :disabled="loading"
            @click="loadWatchlists"
          >
            {{ loading ? "Loading..." : "Refresh" }}
          </button>
        </div>
      </div>
  
      <div
        v-if="loading"
        class="state-card"
      >
        Loading watchlists...
      </div>
  
      <div
        v-else-if="errorMessage"
        class="state-card error"
      >
        <strong>
          Unable to load watchlists.
        </strong>
  
        <span>
          {{ errorMessage }}
        </span>
      </div>
  
      <div
        v-else-if="watchlists.length === 0"
        class="state-card empty"
      >
        <div class="empty-icon">
          <span class="material-icons-outlined">
            visibility
          </span>
        </div>
  
        <div>
          <h4>
            No watchlists yet
          </h4>
  
          <p>
            Use Add Watchlist to create the first Watchlist intake.
          </p>
        </div>
      </div>

      <div
        v-else-if="filteredWatchlists.length === 0"
        class="state-card"
      >
        No watchlists match the current search and status filter.
      </div>
  
      <div
        v-else
        class="watchlist-table-wrapper"
      >
        <table class="watchlist-table">
  
          <thead>
            <tr>
              <th>
                Watchlist
              </th>
  
              <th>
                Status
              </th>
  
              <th>
                Questions
              </th>
  
              <th>
                Submissions
              </th>
  
              <th>
                Actions
              </th>
            </tr>
          </thead>
  
          <tbody>
            <tr
              v-for="watchlist in filteredWatchlists"
              :key="watchlist.id"
            >
              <td>
                <div class="watchlist-name">
                  {{ watchlist.name }}
                </div>
  
                <div
                  v-if="watchlist.description"
                  class="watchlist-description"
                >
                  {{ shortenedDescription(watchlist.description) }}
                </div>
  
              </td>
  
              <td>
                <span
                  class="status-pill"
                  :class="watchlist.publicationStatus"
                >
                  {{ publicationStatusLabel(watchlist.publicationStatus) }}
                </span>
              </td>
  
              <td>
                {{ watchlist.questions.length }}
              </td>
  
              <td>
                {{ watchlist.submissionCount }}
              </td>
  
              <td>
                <button
                  type="button"
                  class="edit-button"
                  :class="watchlist.publicationStatus"
                  @click="$emit('edit', watchlist)"
                >
                  {{ actionLabel(watchlist.publicationStatus) }}
                </button>
              </td>
            </tr>
          </tbody>
  
        </table>
      </div>
  
    </section>
  </template>
  
  <script>
  import {
    computed,
    onMounted,
    ref,
    watch
  } from "vue";
  
  import {
    fetchWatchlists
  } from "@/firebase/firestore";
  
  export default {
    name:
      "WatchlistDetails",
  
    props: {
      refreshKey: {
        type:
          Number,
        default:
          0
      }
    },
  
    emits: [
      "edit"
    ],
  
    setup(props) {
  
      const watchlists =
        ref([]);
  
      const loading =
        ref(false);
  
      const errorMessage =
        ref("");

      const searchText =
        ref("");

      const statusFilter =
        ref("");

      const filteredWatchlists =
        computed(() => {
          const search =
            searchText.value
              .trim()
              .toLowerCase();

          const statusPriority = {
            draft: 0,
            inReview: 1,
            published: 2,
            archived: 2
          };

          return watchlists.value.filter(watchlist => {
            if (
              statusFilter.value &&
              watchlist.publicationStatus !== statusFilter.value
            ) {
              return false;
            }

            if (!search) { return true; }

            return String(watchlist.name ?? "")
              .toLowerCase()
              .includes(search);
          }).sort((first, second) => {
            const statusDifference =
              (statusPriority[first.publicationStatus] ?? 2) -
              (statusPriority[second.publicationStatus] ?? 2);
            if (statusDifference !== 0) { return statusDifference; }

            const firstUpdated = first.updatedAt?.toMillis?.() ?? 0;
            const secondUpdated = second.updatedAt?.toMillis?.() ?? 0;
            return secondUpdated - firstUpdated;
          });
        });

      const publicationStatusLabel = status => ({
        draft: "Draft",
        inReview: "In Review",
        published: "Published",
        archived: "Archived"
      }[status] ?? "Draft");

      const actionLabel = status => {
        if (status === "draft") { return "Draft"; }
        if (status === "inReview") { return "Review"; }
        return "Edit";
      };
  
      const loadWatchlists =
        async () => {
  
          if (loading.value) {
            return;
          }
  
          loading.value =
            true;
  
          errorMessage.value =
            "";
  
          try {
  
            watchlists.value =
              await fetchWatchlists();
  
          } catch (error) {
  
            console.error(
              "❌ Unable to load watchlists:",
              error
            );
  
            errorMessage.value =
              error?.message ??
              "An unknown error occurred.";
  
          } finally {
  
            loading.value =
              false;
          }
        };
  
      const shortenedDescription =
        description => {
  
          const value =
            String(
              description ??
              ""
            ).trim();
  
          if (
            value.length <=
            55
          ) {
  
            return value;
          }
  
          return `${value.slice(
            0,
            52
          )}...`;
        };
  
      onMounted(
        loadWatchlists
      );
  
      watch(
        () =>
          props.refreshKey,
        loadWatchlists
      );
  
      return {
        watchlists,
        filteredWatchlists,
        loading,
        errorMessage,
        searchText,
        statusFilter,
        loadWatchlists,
        shortenedDescription,
        publicationStatusLabel,
        actionLabel
      };
    }
  };
  </script>
  
  <style scoped>
  .watchlist-details {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-height: 0;
  }
  
  .details-toolbar {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .toolbar-controls {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 10px;
  }

  .watchlist-search {
    display: flex;
    align-items: center;
    min-width: 220px;
    flex: 1;
    padding: 0 11px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #87908b;
  }

  .watchlist-search .material-icons-outlined {
    flex-shrink: 0;
    font-size: 20px;
  }

  .watchlist-search input {
    min-width: 0;
    width: 100%;
    padding: 9px 8px;
    border: 0;
    outline: 0;
    background: transparent;
    color: #25322c;
    font: inherit;
  }

  .status-filter {
    min-width: 145px;
    padding: 9px 11px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #25322c;
    font: inherit;
  }
  
  .refresh-button {
    flex-shrink: 0;
    padding: 9px 15px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #25322c;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    transition:
      background-color 0.18s ease,
      opacity 0.18s ease;
  }
  
  .refresh-button:hover:not(:disabled) {
    background: #f4f8f5;
  }
  
  .refresh-button:disabled {
    cursor: wait;
    opacity: 0.65;
  }
  
  .state-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
    color: #737d78;
  }
  
  .state-card.error {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
    border-color: #efc5c5;
    background: #fff7f7;
    color: #a33636;
  }
  
  .state-card.empty {
    min-height: 130px;
  }
  
  .state-card h4 {
    margin: 0;
    color: #25322c;
    font-size: 1rem;
  }
  
  .state-card p {
    margin: 5px 0 0;
  }
  
  .empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    border-radius: 12px;
    background: #edf7f1;
    color: #178554;
  }
  
  .empty-icon span {
    font-size: 25px;
  }
  
  .watchlist-table-wrapper {
    overflow-x: auto;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
  }
  
  .watchlist-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .watchlist-table th,
  .watchlist-table td {
    padding: 16px 18px;
    border-bottom: 1px solid #edf0ee;
    text-align: left;
    vertical-align: top;
  }
  
  .watchlist-table th {
    background: #f7f9f8;
    color: #59645f;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  
  .watchlist-table tbody tr:last-child td {
    border-bottom: 0;
  }
  
  .watchlist-table tbody tr:hover {
    background: #fbfdfb;
  }
  
  .watchlist-name {
    color: #1c2923;
    font-weight: 600;
  }
  
  .watchlist-description {
    max-width: 560px;
    margin-top: 5px;
    color: #68736d;
    font-size: 0.88rem;
    line-height: 1.4;
  }
  
  .status-pill {
    display: inline-flex;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 600;
  }
  
  .status-pill.published {
    background: #edf7f1;
    color: #178554;
  }
  
  .status-pill.draft {
    background: #f1f2f1;
    color: #727a76;
  }

  .status-pill.inReview {
    background: #fff3e8;
    color: #ad5514;
  }

  .status-pill.archived {
    background: #eef1f0;
    color: #606a65;
  }
  
  .edit-button {
    padding: 8px 13px;
    border: 1px solid #c8ded2;
    border-radius: 8px;
    background: #edf7f1;
    color: #178554;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.18s ease;
  }
  
  .edit-button:hover {
    background: #dff1e7;
  }

  .edit-button.draft {
    border-color: rgba(195, 45, 58, 0.35);
    background: rgba(220, 53, 69, 0.11);
    color: #a33636;
  }

  .edit-button.inReview {
    border-color: rgba(224, 112, 20, 0.35);
    background: rgba(243, 126, 32, 0.12);
    color: #ad5514;
  }
  
  @media (max-width: 700px) {
    .toolbar-controls {
      align-items: stretch;
      flex-direction: column;
    }

    .watchlist-search,
    .status-filter {
      width: 100%;
      box-sizing: border-box;
    }
  
    .refresh-button {
      width: 100%;
    }
  }
  </style>
